import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, ShieldCheck, X } from 'lucide-react';
import { toast } from 'sonner';
import { z } from 'zod';
import { Button } from './components/ui/Button';
import { Card } from './components/ui/Card';
import { Input } from './components/ui/Input';
import { useApi } from './lib/api';
import { typography } from './theme';
import { useUserControlsContext } from './contexts/UserControlsContext';
import { hasRequiredRole } from './lib/roles';
import {
  describeRoleError,
  fetchAdminRoleRoster,
  setAdminRole,
} from './api/admin';
import type { AdminRoleRosterResponse, RoleRosterEntry } from '@shared/schemas/admin-access';
import { useAdminUsers } from './api/useAdminUsers';

// ─── Helpers ─────────────────────────────────────────────────────────

/** The one role whose grant can create a user: POST /api/admin/roles mints
 *  the row for an external email that has none (src/api/admin.ts). */
const EXTERNAL_SIGN_IN_ROLE = 'external_sign_in';
const EmailSchema = z.string().email();

function displayName(user: { ownerEmail: string | null; slackUserId: string }): string {
  return user.ownerEmail || user.slackUserId;
}

function formatGrantedAt(value: string | Date): string {
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleDateString();
}

// ─── Component ───────────────────────────────────────────────────────

export default function AdminRoles() {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  const userControls = useUserControlsContext();

  // Grant/revoke is admin-only server-side (POST/DELETE /api/admin/roles), so
  // a user_admin sees the roster read-only rather than buttons that 403.
  // hasRequiredRole takes in the userRoles and a list of roles that the userRoles
  // should contain. An empty list for the second param is the admin role case
  // only if userControls.roles has the admin role will this be true 
  const isFullAdmin = hasRequiredRole(userControls.roles, []);

  const [filter, setFilter] = useState('');
  const [grantingRoleSlug, setGrantingRoleSlug] = useState<string | null>(null);
  const [grantSearch, setGrantSearch] = useState('');

  const rosterQuery = useQuery<AdminRoleRosterResponse>({
    queryKey: ['admin', 'roles', 'roster'],
    queryFn: () => fetchAdminRoleRoster(apiFetch),
    staleTime: 0,
  });

  const usersQuery = useAdminUsers({ enabled: isFullAdmin, staleTime: 0 });

  const mutateRoleMutation = useMutation({
    mutationFn: async (payload: { email: string; roleSlug: string; grant: boolean }) => {
      try {
        return { ...payload, body: await setAdminRole(apiFetch, payload) };
      } catch (error) {
        // The toast surface below renders this message, so it must be the
        // shared safe copy — never the server's `detail`, which is the raw
        // caught exception on a 500.
        throw new Error(describeRoleError(error, 'update'));
      }
    },
    onSuccess: (result) => {
      toast.success(`${result.roleSlug} ${result.grant ? 'granted to' : 'revoked from'} ${result.email}`);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'roles'] });
      // Self-grant is possible, and /api/me is cached with staleTime: Infinity
      // — without this the acting admin's own tab bar / route guards stay stale.
      void queryClient.invalidateQueries({ queryKey: ['/api/me'] });
    },
    // No onError: the MutationCache.onError → notifyError path is the single
    // error-toast surface, and it surfaces the thrown message above.
  });

  const roles = useMemo(() => rosterQuery.data?.roles ?? [], [rosterQuery.data?.roles]);

  // Filter matches the role itself (name/slug) or any of its holders, so the
  // same box answers both "who has admin?" and "what does alice have?".
  const filteredRoles = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return roles;
    return roles.filter(
      (role) =>
        role.name.toLowerCase().includes(q) ||
        role.slug.toLowerCase().includes(q) ||
        role.members.some((m) => displayName(m).toLowerCase().includes(q)),
    );
  }, [roles, filter]);

  const totalGrants = useMemo(() => roles.reduce((sum, r) => sum + r.memberCount, 0), [roles]);

  const handleRevoke = (role: RoleRosterEntry, member: RoleRosterEntry['members'][number]) => {
    if (!member.ownerEmail) return;
    if (!window.confirm(`Revoke "${role.name}" from ${member.ownerEmail}?`)) return;
    mutateRoleMutation.mutate({ email: member.ownerEmail, roleSlug: role.slug, grant: false });
  };

  const handleGrant = (role: RoleRosterEntry, email: string) => {
    mutateRoleMutation.mutate({ email, roleSlug: role.slug, grant: true });
    setGrantingRoleSlug(null);
    setGrantSearch('');
  };

  const grantableUsers = (role: RoleRosterEntry) => {
    const held = new Set(role.members.map((m) => m.id));
    const q = grantSearch.trim().toLowerCase();
    return (usersQuery.data?.users ?? [])
      .filter((u) => u.ownerEmail && !held.has(u.id))
      .filter((u) => !q || displayName(u).toLowerCase().includes(q))
      .slice(0, 50);
  };

  // For external_sign_in only: the typed email, when it is a valid email that
  // no user holds yet, so the picker can offer to add (and mint) it.
  const addableEmail = (role: RoleRosterEntry): string | null => {
    if (role.slug !== EXTERNAL_SIGN_IN_ROLE) return null;
    const q = grantSearch.trim().toLowerCase();
    if (!EmailSchema.safeParse(q).success) return null;
    const known = (usersQuery.data?.users ?? []).some((u) => u.ownerEmail?.toLowerCase() === q);
    return known ? null : q;
  };

  if (rosterQuery.isLoading) {
    return (
      <Card>
        <div className="animate-pulse space-y-4">
          <div className="h-4 w-40 rounded bg-nightshade-200" />
          <div className="h-20 rounded bg-nightshade-100" />
        </div>
      </Card>
    );
  }

  if (rosterQuery.error) {
    return <div className="text-content-danger">Error: {describeRoleError(rosterQuery.error, 'load')}</div>;
  }

  return (
    <div>
      <h2 className={`${typography.headingMd} mb-1`}>Roles</h2>
      <p className="mb-4 text-sm text-content-secondary">
        Every role and who currently holds it — {roles.length} roles, {totalGrants} active grants.{' '}
        {isFullAdmin
          ? 'Grant or revoke directly from each role.'
          : 'Read-only: granting and revoking requires the admin role.'}
      </p>

      <Input
        placeholder="Filter by role or user email..."
        value={filter}
        onChange={(e) => setFilter((e.target as HTMLInputElement).value)}
        className="mb-4"
      />

      <div className="space-y-3">
        {filteredRoles.length === 0 ? (
          <p className="text-sm text-content-tertiary py-8 text-center">No matching roles</p>
        ) : (
          filteredRoles.map((role) => (
            <Card key={role.id}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4 shrink-0 text-nightshade-400" />
                    <h3 className={typography.headingSm}>{role.name}</h3>
                    <span className="font-mono text-xs text-content-tertiary">{role.slug}</span>
                    <span className="px-1.5 py-0.5 rounded-full bg-nightshade-100 text-nightshade-600 text-[11px] font-medium">
                      {role.memberCount}
                    </span>
                  </div>
                  {role.description && (
                    <p className="mt-1 ml-6 text-xs text-content-tertiary">{role.description}</p>
                  )}
                </div>
                {isFullAdmin && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setGrantingRoleSlug(grantingRoleSlug === role.slug ? null : role.slug);
                      setGrantSearch('');
                    }}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Grant
                  </Button>
                )}
              </div>

              {/* Grant picker */}
              {isFullAdmin && grantingRoleSlug === role.slug && (
                <div className="mb-3 p-3 rounded-lg bg-nightshade-50">
                  <Input
                    placeholder="Search users by email..."
                    value={grantSearch}
                    onChange={(e) => setGrantSearch((e.target as HTMLInputElement).value)}
                    className="mb-2"
                    autoFocus
                  />
                  <div className="border border-secondary rounded-lg bg-white max-h-[200px] overflow-y-auto">
                    {usersQuery.isLoading ? (
                      <p className="text-xs text-content-tertiary p-3">Loading users…</p>
                    ) : grantableUsers(role).length === 0 && !addableEmail(role) ? (
                      <p className="text-xs text-content-tertiary p-3">No matching users.</p>
                    ) : (
                      <>
                        {addableEmail(role) && (
                          <button
                            onClick={() => handleGrant(role, addableEmail(role)!)}
                            disabled={mutateRoleMutation.isPending}
                            className="w-full text-left px-3 py-1.5 text-sm hover:bg-nightshade-50 transition-colors flex items-center justify-between gap-2 border-b border-secondary last:border-b-0"
                          >
                            <span className="truncate text-content-primary">Add {addableEmail(role)}</span>
                            <Plus className="h-3.5 w-3.5 shrink-0 text-nightshade-400" />
                          </button>
                        )}
                        {grantableUsers(role).map((u) => (
                          <button
                            key={u.id}
                            onClick={() => handleGrant(role, u.ownerEmail!)}
                            disabled={mutateRoleMutation.isPending}
                            className="w-full text-left px-3 py-1.5 text-sm hover:bg-nightshade-50 transition-colors flex items-center justify-between gap-2 border-b border-secondary last:border-b-0"
                          >
                            <span className="truncate text-content-secondary">{displayName(u)}</span>
                            <Plus className="h-3.5 w-3.5 shrink-0 text-nightshade-400" />
                          </button>
                        ))}
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Members */}
              {role.members.length === 0 ? (
                <p className="text-sm text-content-tertiary">No users hold this role.</p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {role.members.map((member) => (
                    <span
                      key={member.id}
                      title={`${member.userType} · granted ${formatGrantedAt(member.grantedAt)}${
                        member.grantedBy ? ` by ${displayName(member.grantedBy)}` : ''
                      }`}
                      className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-full bg-nightshade-50 ring-1 ring-nightshade-200 text-xs text-content-primary"
                    >
                      {displayName(member)}
                      {isFullAdmin && (
                        <button
                          onClick={() => handleRevoke(role, member)}
                          disabled={mutateRoleMutation.isPending || !member.ownerEmail}
                          title={
                            member.ownerEmail
                              ? `Revoke ${role.name}`
                              : 'This user has no email — revoke via the API'
                          }
                          className="text-nightshade-400 hover:text-pomegranate-600 disabled:opacity-40"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </span>
                  ))}
                </div>
              )}
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
